import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import html2pdf from 'html2pdf.js';
import { Printer, Download, ArrowLeft } from 'lucide-react';
import '../letterstyles/AgreeementLetter.css';

const AgreementLetter = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const printRef = useRef(null);

  const superAdminId = Number(localStorage.getItem('adminuserId')) || 0;

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `${state?.user?.fullName || 'Employee'}__Agreement_Letter`,
  });

  const handleDownloadPDF = () => {
    const element = printRef.current;
    if (!element) return;

    const opt = {
      margin: 0,
      filename: `${state?.user?.fullName || 'Employee'}_Agreement_Letter.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2.5,
        useCORS: true,
        allowTaint: true,
        scrollY: 0,
        logging: false,
        letterRendering: true,
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
        compressPdf: true,
      },
      pagebreak: {
        mode: ['avoid-all', 'css', 'legacy'],
        avoid: ['.al-doc-header', '.al-doc-sign-block', '.al-doc-acceptance', '.al-doc-footer'],
      },
    };

    html2pdf().set(opt).from(element).save();
  };

  useEffect(() => {
    if (!state?.user) {
      alert('No user data provided');
      navigate('/');
    }

    const fetchCompany = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `https://payyug.in/api/get-company?superAdminId=${superAdminId}`
        );
        const data = await response.json();

        if (response.ok && data) {
          if (data.logo) {
            data.logoUrl = `https://payyug.in/${data.logo.replace(/^images\//, '')}`;
          }
          setCompany(data);
        } else {
          console.error('Failed to fetch company data');
        }
      } catch (err) {
        console.error('Error fetching company details:', err);
      } finally {
        setLoading(false);
      }
    };

    if (superAdminId) fetchCompany();
  }, [navigate, state?.user, superAdminId]);

  const user = state?.user;
  if (!user) return null;
  if (loading) {
    return (
      <div className="al-doc-loading">
        <p>Loading company details...</p>
      </div>
    );
  }

  return (
    <div className="al-doc-wrapper">
      <div className="al-doc-action-bar">
        <button
          type="button"
          className="al-doc-btn al-doc-btn-back"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={16} /> Back
        </button>

        <div className="al-doc-action-group">
          <button
            type="button"
            className="al-doc-btn al-doc-btn-print"
            onClick={handlePrint}
          >
            <Printer size={16} /> Print
          </button>
          <button
            type="button"
            className="al-doc-btn al-doc-btn-download"
            onClick={handleDownloadPDF}
          >
            <Download size={16} /> Download
          </button>
        </div>
      </div>

      <div className="al-doc-sheet" ref={printRef}>
        {/* Header */}
        <div className="al-doc-header">
          <img
            src={company?.logoUrl || '/default-logo.png'}
            alt="Company Logo"
            className="al-doc-logo"
          />
          <div className="al-doc-company-info">
            <h2>{company?.name}</h2>
            <p><strong>CIN:</strong> {company?.cin || 'N/A'}</p>
            <p><strong>Email:</strong> {company?.email || 'N/A'}</p>
            <p><strong>Regt. Office:</strong> {company?.address || 'N/A'}</p>
          </div>
        </div>

        {/* Heading */}
        <div className="al-doc-title-wrapper">
          <h2>Employee Agreement Letter</h2>
        </div>

        {/* Body */}
        <div className="al-doc-body">
          <p className="al-doc-date">
            <strong>Date:</strong> {new Date().toLocaleDateString('en-GB')}
          </p>
          <p>To,</p>
          <p><strong>{user.fullName}</strong></p>
          <p>{user.branch}</p>
          <p><strong>{user.address}</strong></p>
          <p><strong>{[user.district, user.state, user.pincode].filter(Boolean).join(', ')}</strong></p>
          <p className="al-doc-subject"><strong>Subject: </strong> Service Agreement Letter</p>
          <p><strong>Dear Mr./Mrs. {user.fullName},</strong></p>

          <p>
            We are pleased to inform you that you are being appointed to the position of{' '}
            <strong>{user.roleName}</strong> at <strong>{company?.name}</strong>. Through this
            letter, we are sharing with you the terms and conditions of the service agreement:
          </p>
          <hr className="al-doc-divider" />

          <p><strong>1. Position and Location</strong></p>
          <p>
            You are being appointed as {user.roleName}, and your workplace will be {user.branchName || user.branch}. As per the
            company’s requirements, you may be transferred to other branches as well.
          </p>
          <hr className="al-doc-divider" />

          <p><strong>2. Date of Commencement</strong></p>
          <p>Your appointment will be considered effective from {new Date().toLocaleDateString('en-GB')}.</p>
          <hr className="al-doc-divider" />

          <p><strong>3. Salary and Allowances</strong></p>
          <p>
            You will be provided a monthly salary of ₹________ (Rupees __________________). In addition,
            other allowances may be provided from time to time as per the company’s policies.
          </p>
          <hr className="al-doc-divider" />

          <p><strong>4. Working Hours</strong></p>
          <p>
            As per company rules, working hours will be from ____ AM/PM to ___ AM/PM. There will be ____
            working days in a week.
          </p>
          <hr className="al-doc-divider" />

          <p><strong>5. Confidentiality</strong></p>
          <p>
            You shall keep all company-related information confidential and will not share it with any
            third party.
          </p>
          <hr className="al-doc-divider" />

          <p><strong>6. Termination of Service</strong></p>
          <p>
            If you wish to leave the company, you must provide at least <strong>30</strong> days prior
            notice or salary in lieu of that period. Similarly, the company will also provide notice
            before terminating your service.
          </p>
          <hr className="al-doc-divider" />

          <p><strong>7. Other Conditions</strong></p>
          <p>
            • You must comply with all company rules and codes of conduct.<br />
            • Any kind of indiscipline may lead to termination of the contract.
          </p>
          <hr className="al-doc-divider" />

          <p>
            Please sign a copy of this letter to indicate your acceptance and return it to us. We wish
            you a bright future with our organization.
          </p>

          <div className="al-doc-sign-block">
            <p><strong>Sincerely,</strong></p>
            <p>Signature: ____________________</p>
            <p>Name: ________________________</p>
            <p>{user.roleName}</p>
            <p>{company?.name}</p>
          </div>

          <div className="al-doc-acceptance">
            <p><strong>Acceptance</strong></p>
            <p>
              I, <strong>{user.fullName}</strong>, have read and agree to all the terms mentioned
              above and accept this appointment.
            </p>
            <div className="al-doc-sign-row">
              <p>Signature: ____________________</p>
              <p>Date: ____________________</p>
            </div>
          </div>
        </div>

        {/* Footer pinned to bottom */}
        <div className="al-doc-footer">
          <hr />
          <p>{company?.name} | CIN: {company?.cin || 'N/A'}</p>
          <p>Email: {company?.email || 'N/A'} | Address: {company?.address || 'N/A'}</p>
        </div>
      </div>
    </div>
  );
};

export default AgreementLetter;